// ──────────────────────────────────────────────────────
// API Client — Q Care AI Kiosk
// Fetch wrapper with timeout, auth header, typed errors
// ──────────────────────────────────────────────────────
import { ScanRequest, ScanResponse } from './types';
import { resolveIdentity, clearRuntimeCredentials, buildKioskHeaders } from './kiosk-identity';

// Same-origin proxy: /api/backend/* → backend via Docker DNS
// No NEXT_PUBLIC env vars needed — proxy route handles upstream.
const API_BASE = '';
const TENANT_ID = process.env.NEXT_PUBLIC_TENANT_ID || 't1';
const STORE_ID = process.env.NEXT_PUBLIC_STORE_ID || 's1';

const TIMEOUT_MS = 15_000; // allow time for backend orchestration + LLM

export type ErrorKind = 'UNAUTHORIZED' | 'TIMEOUT' | 'NETWORK' | 'SERVER' | 'UNKNOWN';

export class KioskApiError extends Error {
    kind: ErrorKind;
    statusCode?: number;

    constructor(kind: ErrorKind, message: string, statusCode?: number) {
        super(message);
        this.name = 'KioskApiError';
        this.kind = kind;
        this.statusCode = statusCode;
    }
}

// ── Arabic / Persian digit → ASCII ───────────────────
// Shared utility — re-exported for backward compatibility.
import { normalizeDigits } from './normalize';
export { normalizeDigits } from './normalize';


// ── Barcode / QR Normalization ────────────────────────
// Handles raw QR content (URLs with embedded barcodes),
// trims whitespace, normalizes Arabic/Persian digits,
// extracts longest digit sequence ≥ 6.
export function normalizeScanInput(raw: string): string {
    const trimmed = normalizeDigits(raw.trim());
    if (!trimmed) return trimmed;

    // Already a clean numeric barcode
    if (/^\d{6,}$/.test(trimmed)) return trimmed;

    // URL-like: extract last path segment or longest digit run
    if (trimmed.includes('/') || trimmed.includes('http')) {
        // Try last path segment first
        const segments = trimmed.split('/').filter(Boolean);
        const last = segments[segments.length - 1];
        if (last && /^\d{6,}$/.test(last)) return last;
    }

    // Extract longest digit sequence ≥ 6 chars
    const matches = trimmed.match(/\d{6,}/g);
    if (matches) {
        return matches.reduce((a, b) => a.length >= b.length ? a : b);
    }

    // Fallback: return as-is
    return trimmed;
}

// ── Barcode Cache ─────────────────────────────────────
// In-memory cache with 10-minute TTL for instant re-scans.
const CACHE_TTL_MS = 10 * 60 * 1000;
const scanCache = new Map<string, { data: ScanResponse; ts: number }>();

function getCached(barcode: string): ScanResponse | null {
    const entry = scanCache.get(barcode);
    if (!entry) return null;
    if (Date.now() - entry.ts > CACHE_TTL_MS) {
        scanCache.delete(barcode);
        return null;
    }
    return entry.data;
}

function setCache(barcode: string, data: ScanResponse): void {
    scanCache.set(barcode, { data, ts: Date.now() });
    // Limit cache size to 100 entries
    if (scanCache.size > 100) {
        const oldest = scanCache.keys().next().value;
        if (oldest) scanCache.delete(oldest);
    }
}

async function fetchWithTimeout(
    url: string,
    options: RequestInit,
    timeoutMs: number = TIMEOUT_MS,
): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetch(url, {
            ...options,
            signal: controller.signal,
        });
        return response;
    } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') {
            throw new KioskApiError('TIMEOUT', 'الطلب أخذ وقت طويل');
        }
        // Network errors (offline, DNS failure, CORS, etc.)
        throw new KioskApiError('NETWORK', 'تعذّر الاتصال بالخادم');
    } finally {
        clearTimeout(timer);
    }
}

async function classifyError(response: Response): Promise<KioskApiError> {
    if (response.status === 401 || response.status === 403) {
        // Stale credentials — clear them and signal the UI to show gate
        const id = resolveIdentity();
        if (id.source === 'localStorage') {
            clearRuntimeCredentials();
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('kiosk-auth-revoked'));
            }
        }
        return new KioskApiError('UNAUTHORIZED', 'مفتاح الكشك غير صحيح', response.status);
    }

    try {
        const body = await response.json();
        const msg = body.message || body.error || `HTTP ${response.status}`;
        return new KioskApiError('SERVER', msg, response.status);
    } catch {
        return new KioskApiError('SERVER', `خطأ في الخادم (${response.status})`, response.status);
    }
}

/**
 * Scan a product barcode via the kiosk API.
 * Returns cached data instantly for repeated scans within 10 minutes.
 */
export async function scanProduct(
    barcode: string,
    skinType?: string,
): Promise<ScanResponse> {
    const normalized = normalizeScanInput(barcode);

    // Check cache first for instant re-scans
    const cached = getCached(normalized);
    if (cached) return cached;

    const identity = resolveIdentity();
    const body: ScanRequest = {
        tenantId: TENANT_ID,
        storeId: STORE_ID,
        kioskId: identity.kioskId,
        barcode: normalized,
    };

    if (skinType) {
        body.context = { skinType };
    }

    const response = await fetchWithTimeout(`${API_BASE}/api/backend/kiosk/scan`, {
        method: 'POST',
        headers: buildKioskHeaders(identity),
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        throw await classifyError(response);
    }

    const data: ScanResponse = await response.json();

    // Cache successful responses
    setCache(normalized, data);

    return data;
}

// ── Fast Scan Cache (2-min TTL for quick re-scans) ─────
const FAST_CACHE_TTL_MS = 2 * 60 * 1000;
const fastScanCache = new Map<string, { data: ScanResponse; ts: number }>();

function getFastCached(barcode: string): ScanResponse | null {
    const entry = fastScanCache.get(barcode);
    if (!entry) return null;
    if (Date.now() - entry.ts > FAST_CACHE_TTL_MS) {
        fastScanCache.delete(barcode);
        return null;
    }
    return entry.data;
}

function setFastCache(barcode: string, data: ScanResponse): void {
    fastScanCache.set(barcode, { data, ts: Date.now() });
    if (fastScanCache.size > 20) {
        const oldest = fastScanCache.keys().next().value;
        if (oldest) fastScanCache.delete(oldest);
    }
}

/**
 * Fast scan: DB-only, no LLM. Returns in <500ms typical.
 * Uses shorter 5s timeout and 2-min cache.
 */
export async function scanProductFast(
    barcode: string,
    skinType?: string,
): Promise<ScanResponse> {
    const normalized = normalizeScanInput(barcode);

    // Check fast cache first
    const cached = getFastCached(normalized);
    if (cached) {
        if (process.env.NODE_ENV === 'development') {
            console.log('[scan-fast] CACHE HIT', normalized);
        }
        return cached;
    }

    const identity = resolveIdentity();
    const body: ScanRequest = {
        tenantId: TENANT_ID,
        storeId: STORE_ID,
        kioskId: identity.kioskId,
        barcode: normalized,
    };

    if (skinType) {
        body.context = { skinType };
    }

    const t0 = Date.now();
    const response = await fetchWithTimeout(`${API_BASE}/api/backend/kiosk/scan_fast`, {
        method: 'POST',
        headers: buildKioskHeaders(identity),
        body: JSON.stringify(body),
    }, 5_000); // 5s timeout for fast path

    if (!response.ok) {
        throw await classifyError(response);
    }

    const data: ScanResponse = await response.json();
    const elapsed = Date.now() - t0;

    // Log timing in dev
    if (process.env.NODE_ENV === 'development') {
        const dbMs = response.headers.get('X-Scan-DB-Ms');
        const totalMs = response.headers.get('X-Scan-Total-Ms');
        const cache = response.headers.get('X-Cache');
        console.log(`[scan-fast] ${normalized} elapsed=${elapsed}ms server_total=${totalMs}ms db=${dbMs}ms cache=${cache}`);
    }

    // Cache the fast response
    setFastCache(normalized, data);
    // Also populate the main cache
    setCache(normalized, data);

    return data;
}

/**
 * Enrich scan: runs LLM for richer cards/upsells.
 * Called asynchronously after scanProductFast.
 * Returns enriched ScanResponse or null on failure.
 */
export async function scanProductEnrich(
    barcode: string,
    skinType?: string,
): Promise<ScanResponse | null> {
    const normalized = normalizeScanInput(barcode);

    const identity = resolveIdentity();
    const body: ScanRequest = {
        tenantId: TENANT_ID,
        storeId: STORE_ID,
        kioskId: identity.kioskId,
        barcode: normalized,
    };

    if (skinType) {
        body.context = { skinType };
    }

    try {
        const t0 = Date.now();
        const response = await fetchWithTimeout(`${API_BASE}/api/backend/kiosk/scan_enrich`, {
            method: 'POST',
            headers: buildKioskHeaders(identity),
            body: JSON.stringify(body),
        }, 12_000); // 12s timeout for LLM

        if (!response.ok) return null;

        const data: ScanResponse = await response.json();
        const elapsed = Date.now() - t0;

        // Log timing in dev
        if (process.env.NODE_ENV === 'development') {
            const aiMs = response.headers.get('X-Scan-AI-Ms');
            const totalMs = response.headers.get('X-Scan-Total-Ms');
            const cache = response.headers.get('X-Cache');
            console.log(`[scan-enrich] ${normalized} elapsed=${elapsed}ms server_total=${totalMs}ms ai=${aiMs}ms cache=${cache}`);
        }

        // Update caches with enriched data
        setFastCache(normalized, data);
        setCache(normalized, data);

        return data;
    } catch {
        // Enrichment is non-critical — swallow errors
        if (process.env.NODE_ENV === 'development') {
            console.log(`[scan-enrich] FAILED for ${normalized} — non-blocking`);
        }
        return null;
    }
}

/**
 * Lightweight health probe to confirm backend connectivity.
 * Returns true if backend responds with { ok: true }.
 */
export async function checkHealth(): Promise<boolean> {
    try {
        const res = await fetch(`${API_BASE}/api/backend/health`, {
            method: 'GET',
            cache: 'no-store',
            signal: AbortSignal.timeout(3000),
        });
        if (!res.ok) return false;
        const body = await res.json();
        return body.ok === true;
    } catch {
        return false;
    }
}
